/**
 * Gerenciador único de cliques/popups (contrato do mapa).
 *
 * Camadas que respondem a cliques têm `layer.set('popup', (feature) => ReactNode | null)`.
 * No clique consultamos as features no pixel (tolerância de 6 px no mouse e 10 px no toque, `toque.ts`), ordenamos
 * pelo zIndex da camada (maior primeiro) e mostramos o primeiro conteúdo não
 * nulo num `Overlay` do OL, renderizado por portal React.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import Overlay from 'ol/Overlay';
import type { FeatureLike } from 'ol/Feature';
import type BaseLayer from 'ol/layer/Base';
import type Layer from 'ol/layer/Layer';
import type OlMap from 'ol/Map';
import type MapBrowserEvent from 'ol/MapBrowserEvent';
import type Feature from 'ol/Feature';
import type Geometry from 'ol/geom/Geometry';
import { GripHorizontal, X } from 'lucide-react';
import { MunicipioFireCount } from '../fires/MunicipioFireCount';
import { formatLonLat } from '../lib/format';
import { useModalLayer } from '../ui/modalStack';
import { ensureDataSource } from '../vectors/loader';
import { useVectorStore } from '../vectors/store';
import { useMapInstance } from './mapContext';
import {
  cabe,
  caberEm,
  colocarFora,
  CONSULTA_CELULAR,
  deslocamentoParaCaber,
  FOLGA_DA_COLUNA_PX,
  intersecao,
  larguraAoLadoDaColuna,
  lugarLimpo,
  medirColuna,
  medirTrilho,
  naAlturaDaColuna,
  pontoNaBorda,
  precisaSair,
  valeMudar,
  type Caixa,
  type Forma,
} from './popupArea';
import { CONSUMIDO, coordinatePopupAt, registerPopupCloser, registerPopupOpener, type AreaAEvitar } from './popupControl';
import { ContextoDaFicha } from './popupForma';
import { mapToLonLat } from './projection';
import { painelSobreOMapa } from './viewControl';
import { toleranciaDoClique } from './toque';
import { ProximosDiasAqui } from '../previsao/ProximosDiasAqui';

export type PopupRenderer = (feature: FeatureLike) => ReactNode | null;

const HIT_TOLERANCE = 6;
/** Cliques a menos disto (px) do topo do mapa abrem o popup para baixo. */
const POPUP_FLIP_PX = 220;
/** Folga do enquadramento em cada borda (a mesma de `popupArea`). */
const POPUP_MARGEM_PX = 16;

/**
 * Altura máxima do popup: a parte VISÍVEL do mapa (a barra do tempo pode
 * cobri-lo no celular) menos as folgas e a seta. Maior que isso, a ficha
 * rola por dentro — antes, em 768 px de tela, o fim dela ficava inalcançável.
 */
/**
 * No celular, a faixa do topo do mapa é reservada: a hora (da imagem ou da
 * previsão), a busca e os avisos (o do rastreio, o "Modelo, não medição")
 * ficam à vista sob qualquer ficha. No desktop, a linha da busca e dos
 * botões. Devolve o y (tela) onde a ficha pode começar.
 */
function topoLivre(vpTop: number): number {
  // Desktop: a linha da marca, da busca e dos botões (12 px de margem + 36 px).
  if (!noCelular()) return vpTop + TOPO_DO_DESKTOP_PX;
  let topo = vpTop + TOPO_DO_CELULAR_PX;
  for (const sel of ['[data-forecast-chip]', '[data-selo-previsao]']) {
    const r = document.querySelector<HTMLElement>(sel)?.getBoundingClientRect();
    if (r && r.height > 0) topo = Math.max(topo, r.bottom);
  }
  return topo;
}

function tetoDoPopup(viewport: HTMLElement): number {
  const vp = viewport.getBoundingClientRect();
  let fundo = vp.bottom;
  const barra = document.querySelector<HTMLElement>('[data-timebar]')?.getBoundingClientRect();
  if (barra && barra.height > 0 && barra.top < fundo && barra.bottom > vp.top) fundo = Math.min(fundo, barra.top);
  return Math.max(160, Math.floor(fundo - topoLivre(vp.top) - 2 * POPUP_MARGEM_PX - 60)); // seta + distância do ponto (até 20 + 7) e o padding do popup (~26)
}

/**
 * Anda o mapa até a ficha caber na parte útil dele — fora da coluna de botões
 * da direita, no celular (ver `popupArea`). Faz o papel do `panIntoView` do
 * OpenLayers, que só conhece uma folga igual nas quatro bordas.
 */
function enquadrar(map: OlMap, ancora: HTMLElement, duracao: number): void {
  const ficha = ancora.querySelector('.amaview-popup')?.getBoundingClientRect();
  if (!ficha || ficha.width <= 0) return;
  const visivel = parteVisivel(map);
  // No celular a ficha não cobre a faixa do topo (ver `topoLivre`).
  const mapa = { ...visivel, top: topoLivre(visivel.top) };
  const coluna = medirColuna();
  const [dx, dy] = deslocamentoParaCaber(ficha, mapa, coluna, medirTrilho(visivel, coluna));
  if (dx === 0 && dy === 0) return;
  const view = map.getView();
  const centro = view.getCenter();
  const px = centro && map.getPixelFromCoordinate(centro);
  if (!px) return;
  const novo = map.getCoordinateFromPixel([px[0]! + dx, px[1]! + dy]);
  if (!novo) return;
  if (duracao > 0) view.animate({ center: novo, duration: duracao });
  else view.setCenter(novo);
}

/** A área a evitar que a camada declara para a ficha de `feature` (`layer.set('popupEvitar', …)`), ou null. */
function evitarOf(layer: BaseLayer, feature: FeatureLike): AreaAEvitar | null {
  const fn = layer.get('popupEvitar');
  if (typeof fn !== 'function') return null;
  const area = (fn as (f: FeatureLike) => AreaAEvitar | null)(feature);
  return typeof area === 'function' ? area : null;
}

const noCelular = () => typeof window !== 'undefined' && !!window.matchMedia?.(CONSULTA_CELULAR).matches;

/** No celular, a linha de botões e da hora no topo do mapa (12 px de margem + 44 px). */
const TOPO_DO_CELULAR_PX = 56;
/** No desktop, a linha da marca, da busca e dos botões no topo do mapa (12 px de margem + 36 px). */
const TOPO_DO_DESKTOP_PX = 48;
/** A ficha encolhe (e rola por dentro) até esta altura para não cobrir um painel; menor que isso, cobre. */
const ALTURA_MINIMA_DA_FICHA_PX = 320;

/** O que a ficha tem em volta do conteúdo que rola: o recheio de cima (com a alça) e o de baixo, e as bordas. */
const RECHEIO_DA_FICHA_PX = 40;

/** Duração (ms) do deslize da ficha quando a área a evitar encosta nela. */
const DESLIZE_MS = 220;

/** A extensão (projeção do mapa) em px da janela; null se o mapa ainda não sabe projetar. */
function caixaDaExtensao(map: OlMap, extensao: readonly number[]): Caixa | null {
  const a = map.getPixelFromCoordinate([extensao[0]!, extensao[3]!]);
  const b = map.getPixelFromCoordinate([extensao[2]!, extensao[1]!]);
  if (!a || !b) return null;
  const vp = map.getViewport().getBoundingClientRect();
  return { left: vp.left + Math.min(a[0]!, b[0]!), top: vp.top + Math.min(a[1]!, b[1]!), right: vp.left + Math.max(a[0]!, b[0]!), bottom: vp.top + Math.max(a[1]!, b[1]!) };
}

/** O retângulo do mapa (px da janela) sem a faixa que o painel lateral cobre, no desktop. */
function parteVisivel(map: OlMap): Caixa {
  const vp = map.getViewport().getBoundingClientRect();
  return { left: vp.left + painelSobreOMapa(), top: vp.top, right: vp.right, bottom: vp.bottom };
}

/** A parte útil do mapa (acima da barra do tempo, com folga) e o que já está sobre ele: painéis, botões, legenda. */
function medirMapa(map: OlMap): { livre: Caixa; ocupadas: Caixa[] } {
  const vp = parteVisivel(map);
  const barra = document.querySelector<HTMLElement>('[data-timebar]')?.getBoundingClientRect();
  const fundo = barra && barra.height > 0 && barra.top < vp.bottom && barra.bottom > vp.top ? barra.top : vp.bottom;
  const m = 8;
  // No celular a coluna de botões da direita é área reservada: a ficha fica ao lado dela.
  const coluna = medirColuna();
  const direita = coluna ? Math.min(vp.right - m, coluna.left - FOLGA_DA_COLUNA_PX) : vp.right - m;
  // E a faixa do topo também: a hora da imagem e o aviso da previsão ficam à vista.
  const topo = topoLivre(vp.top) + m;
  // No celular o play flutua no meio do pé do mapa: a ficha não o cobre (o toque nele tem de chegar).
  let piso = fundo;
  if (noCelular()) {
    const play = document.querySelector<HTMLElement>('[data-play-flutuante]')?.getBoundingClientRect();
    if (play && play.height > 0 && play.top < piso) piso = play.top;
  }
  // Deitado, o trilho de camadas da esquerda também é reservado (em pé a ficha pode cobri-lo: `medirTrilho`).
  const trilho = medirTrilho(vp, coluna);
  const esquerda = trilho ? Math.max(vp.left + m, trilho.right + FOLGA_DA_COLUNA_PX) : vp.left + m;
  const livre = { left: esquerda, top: topo, right: direita, bottom: piso - m };
  // Os irmãos do mapa dentro do <main> são o que o app põe por cima dele.
  const alvo = map.getTargetElement();
  const ocupadas: Caixa[] = [];
  for (const el of alvo?.parentElement?.children ?? []) {
    // A moldura da previsão contorna o mapa inteiro: é borda, não ocupa lugar.
    if (el === alvo || el.hasAttribute('data-moldura-previsao')) continue;
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) ocupadas.push({ left: r.left, top: r.top, right: r.right, bottom: r.bottom });
  }
  return { livre, ocupadas };
}

function popupOf(layer: BaseLayer | null | undefined): PopupRenderer | null {
  const fn = layer?.get('popup');
  return typeof fn === 'function' ? (fn as PopupRenderer) : null;
}

const hasPopup = (layer: Layer) => popupOf(layer) !== null;

/** Deslocamento (px) do popup arrastado em relação à posição natural. */
export interface Arrasto {
  dx: number;
  dy: number;
}

/** Quanto o ponteiro precisa andar para contar como arrasto (e não clique). */
const ARRASTO_MIN_PX = 3;

/** Município (feature) que contém a coordenada, com a geometria (para contar os focos). */
async function municipioFeatureAt(x: number, y: number): Promise<Feature<Geometry> | null> {
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  const source = await ensureDataSource('municipios');
  if (!source) return null;
  for (const f of source.getFeaturesAtCoordinate([x, y])) {
    const nome = f.get('nome');
    if (typeof nome === 'string' && nome) return f as Feature<Geometry>;
  }
  return null;
}

/** Popup de reserva: o município do ponto clicado (quando nenhuma camada respondeu). */
function MunicipioCard(props: {
  nome: string;
  uf: string | null;
  cdMun: string | null;
  lonLat: [number, number];
  geometry: Geometry | null;
}) {
  const { nome, uf, cdMun, lonLat, geometry } = props;
  return (
    <div className="space-y-0.5">
      <div className="text-xs text-ink-400">Município</div>
      <div className="font-semibold text-white">
        {nome}
        {uf && <span className="font-normal text-ink-300">/{uf}</span>}
      </div>
      <div className="text-xs text-ink-300">
        {formatLonLat(lonLat[0], lonLat[1])}
        {cdMun && <> · IBGE {cdMun}</>}
      </div>
      <MunicipioFireCount cdMun={cdMun} geometry={geometry} />
      <ProximosDiasAqui lon={lonLat[0]} lat={lonLat[1]} />
    </div>
  );
}

export function PopupHost() {
  const map = useMapInstance((s) => s.map);
  const element = useMemo(() => {
    const el = document.createElement('div');
    el.className = 'amaview-popup-anchor';
    return el;
  }, []);
  const overlayRef = useRef<Overlay | null>(null);
  const [content, setContent] = useState<ReactNode | null>(null);
  const [below, setBelow] = useState(false);
  // Fichas móveis (rastreio e previsão): o usuário arrasta a ficha para longe do
  // ponto, e uma linha a mantém ligada a ele. O overlay continua preso à
  // coordenada — mexer o mapa leva a ficha junto.
  const [movel, setMovel] = useState(false);
  const [arrasto, setArrasto] = useState<Arrasto>({ dx: 0, dy: 0 });
  const baseOffset = useRef<[number, number]>([0, -14]);
  // Anel de destaque (foco escolhido na lista "Últimos focos"); some com o popup.
  const markerElement = useMemo(() => {
    const el = document.createElement('div');
    el.className = 'pointer-events-none relative size-0';
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML =
      '<span class="absolute -top-4 -left-4 size-8 rounded-full border-2 border-white shadow-[0_0_0_2px_rgba(7,11,20,0.6)]"></span>' +
      '<span class="absolute -top-4 -left-4 size-8 animate-ping rounded-full border-2 border-fogo-400" style="animation-iteration-count:3"></span>';
    return el;
  }, []);
  const markerRef = useRef<Overlay | null>(null);
  const arrastadaRef = useRef(false);
  // Área que a ficha não deve cobrir (desktop; só as fichas que a declaram).
  const evitarRef = useRef<AreaAEvitar | null>(null);
  /** Altura (do conteúdo) a que a ficha encolheu para não cobrir um painel; volta a null a cada ficha. */
  const tetoReduzido = useRef<number | null>(null);
  const belowRef = useRef(false);
  const deslize = useRef(0);
  /** O usuário pôs a ficha onde quis (arrasto ou teclado), depois de ela abrir. */
  const postaAMao = useRef(false);
  /** O usuário está segurando a ficha (arrasto pela alça, ou tecla de seta apertada). */
  const segurando = useRef(false);
  /** Altura em que a ficha foi enquadrada pela última vez, e se o usuário arrastou o mapa depois de abri-la. */
  const alturaEnquadrada = useRef(0);
  const mapaMexido = useRef(false);
  /**
   * A forma da ficha: completa, ou compacta quando a completa não tem lugar
   * limpo ao lado da área a evitar (desktop). Volta a completa a cada ficha
   * aberta; não volta sozinha no meio do player, para não piscar.
   */
  const [forma, setForma] = useState<Forma>('completa');
  const formaRef = useRef<Forma>('completa');
  formaRef.current = forma;
  /** Os detalhes da ficha compacta foram abertos pelo usuário: aberta assim, ela pode cobrir a área. */
  const detalhesAbertos = useRef(false);
  /** Tamanho da ficha, para a linha de ligação encostar na borda certa. */
  const [tamanho, setTamanho] = useState({ largura: 0, altura: 0 });

  /**
   * Põe a ficha no lugar. Com área a evitar: fora dela. `abrindo`: a ficha
   * acabou de abrir sobre o ponto (ou mudou de forma), e vai de uma vez;
   * depois disso ela só se mexe se passar a cobrir a área (o sistema andou no
   * player, o usuário moveu o mapa ou a largou em cima) ou a não caber na
   * parte útil do mapa (cresceu), e desliza até o novo lugar. Enquanto o
   * usuário a segura, nada a move.
   */
  const desviarRef = useRef<(abrindo: boolean) => void>(() => {});
  desviarRef.current = (abrindo) => {
    const overlay = overlayRef.current;
    const evitar = evitarRef.current;
    if (!map || !overlay || !evitar || segurando.current) return;
    const posicao = overlay.getPosition();
    const ficha = element.querySelector('.amaview-popup')?.getBoundingClientRect();
    if (!posicao || !ficha || ficha.width <= 0) return;
    const ancora = map.getPixelFromCoordinate(posicao);
    if (!ancora) return;
    const { livre, ocupadas } = medirMapa(map);
    // A ficha não passa da parte útil do mapa (nem da altura a que encolheu para não cobrir
    // um painel): o que sobrar rola por dentro dela.
    const tetoLivre = Math.floor(livre.bottom - livre.top - RECHEIO_DA_FICHA_PX);
    element.style.setProperty('--popup-teto', `${Math.max(120, Math.min(tetoLivre, tetoReduzido.current ?? Infinity))}px`);
    const vp = map.getViewport().getBoundingClientRect();
    const mover = (caixa: Caixa) => {
      // O overlay prende a ficha ao ponto pelo meio da borda de baixo (ou de cima, aberta para baixo).
      const alvo = {
        dx: (caixa.left + caixa.right) / 2 - (vp.left + ancora[0]!) - baseOffset.current[0],
        dy: (belowRef.current ? caixa.top : caixa.bottom) - (vp.top + ancora[1]!) - baseOffset.current[1],
      };
      const por = (a: Arrasto) => {
        overlay.setOffset([baseOffset.current[0] + a.dx, baseOffset.current[1] + a.dy]);
        setArrasto(a);
      };
      cancelAnimationFrame(deslize.current);
      const calmo = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      if (abrindo || calmo) {
        por(alvo);
        return;
      }
      const [x0, y0] = overlay.getOffset();
      const de = { dx: x0! - baseOffset.current[0], dy: y0! - baseOffset.current[1] };
      const inicio = performance.now();
      const passo = (agora: number) => {
        // O usuário pegou a ficha no meio do deslize: ela é dele.
        if (segurando.current) return;
        const t = Math.min(1, (agora - inicio) / DESLIZE_MS);
        const k = 1 - (1 - t) ** 3;
        por({ dx: de.dx + (alvo.dx - de.dx) * k, dy: de.dy + (alvo.dy - de.dy) * k });
        if (t < 1) deslize.current = requestAnimationFrame(passo);
      };
      deslize.current = requestAnimationFrame(passo);
    };

    const extensao = evitar();
    const area = extensao && caixaDaExtensao(map, extensao);
    const dentro = cabe(ficha, livre);
    if (!area) {
      // Sem a área na tela (o sistema não existe neste quadro): a ficha fica onde está — mas
      // inteira na parte útil do mapa (o aviso de ausência a faz crescer).
      if (!abrindo && !dentro) mover(caberEm(ficha, livre));
      return;
    }
    // No celular não vale o "nunca cobrir" (a tela não tem para onde a ficha correr): onde o
    // usuário a pôs, ela fica, mesmo sobre o sistema. E, em qualquer tela, a ficha que o usuário
    // fez crescer abrindo os detalhes pode cobrir: é escolha dele. Só tem de caber no mapa.
    const livreParaCobrir = (postaAMao.current && noCelular()) || (detalhesAbertos.current && formaRef.current === 'compacta' && !noCelular());
    if (!abrindo && livreParaCobrir) {
      if (!dentro) mover(caberEm(ficha, livre));
      return;
    }
    // Um painel abriu por cima da ficha (o cartão do rastreio, com a ficha já aberta)? Ela sai de
    // baixo dele, se houver lugar limpo — a não ser que o usuário a tenha posto ali.
    const sobPainel = postaAMao.current ? 0 : ocupadas.reduce((soma, o) => soma + intersecao(ficha, o), 0);
    if (!abrindo && dentro && !precisaSair(ficha, area) && sobPainel === 0) return;
    let nova = colocarFora(area, { largura: ficha.width, altura: ficha.height }, livre, ocupadas);
    // Inteira ela cobriria um painel (a legenda, a lista dos focos, o cartão do rastreio)? Encolhe
    // e rola por dentro, se assim achar lugar limpo; até ALTURA_MINIMA_DA_FICHA_PX.
    if (!lugarLimpo(nova) && (abrindo || !postaAMao.current)) {
      for (const fator of [0.85, 0.7, 0.55]) {
        const altura = Math.floor(ficha.height * fator);
        if (altura < ALTURA_MINIMA_DA_FICHA_PX) break;
        const menor = colocarFora(area, { largura: ficha.width, altura }, livre, ocupadas);
        if (lugarLimpo(menor)) {
          tetoReduzido.current = altura - RECHEIO_DA_FICHA_PX;
          element.style.setProperty('--popup-teto', `${tetoReduzido.current}px`);
          nova = menor;
          break;
        }
      }
    }
    if (!abrindo && dentro && !precisaSair(ficha, area)) {
      if (nova.cobre === 0 && nova.ocupado < sobPainel) mover(nova.caixa);
      return;
    }
    // Desktop: a ficha completa não tem lugar limpo (cobriria o sistema, ou o cartão do rastreio)?
    // Passa à forma compacta, bem menor, e tenta de novo quando ela estiver desenhada.
    if (!noCelular() && formaRef.current === 'completa' && !lugarLimpo(nova)) {
      setForma('compacta');
      return;
    }
    if (!abrindo && dentro && !valeMudar(ficha, nova, area)) return;
    mover(nova.caixa);
  };

  useEffect(() => {
    if (!map) return;
    // Sem autoPan aqui: o conteúdo é desenhado pelo React depois do setPosition;
    // o enquadramento é feito em `panIntoView` quando o conteúdo já tem tamanho.
    const overlay = new Overlay({
      element,
      positioning: 'bottom-center',
      offset: [0, -14],
      stopEvent: true,
    });
    overlayRef.current = overlay;
    map.addOverlay(overlay);
    const marker = new Overlay({ element: markerElement, positioning: 'center-center', stopEvent: false });
    markerRef.current = marker;
    map.addOverlay(marker);
    // Os contêineres de overlays do OL (z-index 0, sem eventos de ponteiro) só
    // têm o popup e o anel: sobem acima da legenda e dos botões do mapa (z-10/z-20)
    // e ficam abaixo das folhas e diálogos (portais z-40+). Sem isso, no celular
    // o popup ficava escondido atrás da legenda.
    map.getOverlayContainerStopEvent().style.zIndex = '25';
    map.getOverlayContainer().style.zIndex = '24';

    const close = () => {
      overlay.setPosition(undefined);
      marker.setPosition(undefined);
      evitarRef.current = null;
      setContent(null);
    };

    // A área a evitar anda (o sistema no player) e o mapa também: a cada desenho do mapa, parado,
    // confere se a ficha passou a cobri-la. Uma conferência por quadro de animação, no máximo.
    let conferencia = 0;
    const conferir = () => {
      if (!evitarRef.current || conferencia) return;
      conferencia = requestAnimationFrame(() => {
        conferencia = 0;
        const view = map.getView();
        if (view.getAnimating() || view.getInteracting()) return;
        desviarRef.current(false);
      });
    };
    map.on('rendercomplete', conferir);
    map.on('moveend', conferir);
    // A ficha muda de tamanho (abrir "Mais detalhes", outro sistema no player): confere de novo,
    // e guarda o tamanho para a linha de ligação.
    /**
     * Ficha que cresce depois de abrir porque os dados chegam depois (a da previsão do
     * ponto, que se marca com `data-reenquadrar-ao-crescer`): reenquadra como na
     * abertura — só ela, só sem área a evitar, sem ter sido arrastada e se o usuário
     * não mexeu no mapa depois de abri-la.
     */
    const aoMudarDeTamanho = () => {
      const r = element.querySelector('.amaview-popup')?.getBoundingClientRect();
      if (r) setTamanho((t) => (Math.abs(t.largura - r.width) < 0.5 && Math.abs(t.altura - r.height) < 0.5 ? t : { largura: r.width, altura: r.height }));
      if (
        r &&
        overlay.getPosition() &&
        !evitarRef.current &&
        !arrastadaRef.current &&
        // Mexer no mapa tira o reenquadramento, a não ser que a ficha cresça a ponto de sair por cima.
        (!mapaMexido.current || r.top < topoLivre(map.getViewport().getBoundingClientRect().top)) &&
        element.querySelector('[data-reenquadrar-ao-crescer]') &&
        r.height > alturaEnquadrada.current + 24
      ) {
        alturaEnquadrada.current = r.height;
        enquadrar(map, element, 250);
      }
      conferir();
    };
    const aoArrastarOMapa = () => {
      if (overlay.getPosition()) mapaMexido.current = true;
    };
    map.on('pointerdrag', aoArrastarOMapa);
    // Zoom pela roda do mouse também é mexer no mapa (a pinça passa pelo `pointerdrag`).
    const viewport = map.getViewport();
    viewport.addEventListener('wheel', aoArrastarOMapa, { passive: true });
    const observador = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(aoMudarDeTamanho) : null;
    observador?.observe(element);
    // Um painel entra ou sai de cima do mapa (o cartão do rastreio): confere de novo.
    const paineis = typeof MutationObserver !== 'undefined' ? new MutationObserver(conferir) : null;
    const pai = map.getTargetElement()?.parentElement;
    if (pai) paineis?.observe(pai, { childList: true });

    const medirTeto = () => {
      element.style.setProperty('--popup-teto', `${tetoDoPopup(map.getViewport())}px`);
      // No celular a ficha cabe AO LADO da coluna de botões da direita, sem cobri-la (e, deitado, do trilho).
      const mapa = map.getViewport().getBoundingClientRect();
      const coluna = medirColuna();
      const largura = larguraAoLadoDaColuna(mapa, coluna, medirTrilho(mapa, coluna));
      if (largura === null) element.style.removeProperty('--popup-largura');
      else element.style.setProperty('--popup-largura', `${largura}px`);
    };
    // Girar a tela ou redimensionar a janela com a ficha aberta: novo teto e reenquadra.
    const aoRedimensionar = () => {
      medirTeto();
      if (overlay.getPosition() && !arrastadaRef.current) requestAnimationFrame(() => enquadrar(map, element, 0));
    };
    map.on('change:size', aoRedimensionar);
    // A barra do tempo muda de altura no celular (expandir/recolher) sem mudar o mapa.
    const barra = document.querySelector<HTMLElement>('[data-timebar]');
    const ro = barra && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(medirTeto) : null;
    if (barra) ro?.observe(barra);

    const show = (coordinate: number[], node: ReactNode, flip: boolean, withMarker = false, podeMover = false, evitar: AreaAEvitar | null = null) => {
      medirTeto();
      cancelAnimationFrame(deslize.current);
      alturaEnquadrada.current = 0;
      mapaMexido.current = false;
      postaAMao.current = false;
      segurando.current = false;
      detalhesAbertos.current = false;
      setForma('completa');
      evitarRef.current = evitar;
      tetoReduzido.current = null;
      belowRef.current = flip;
      const gap = withMarker ? 20 : 14;
      overlay.setPositioning(flip ? 'top-center' : 'bottom-center');
      baseOffset.current = flip ? [0, gap] : [0, -gap];
      overlay.setOffset(baseOffset.current);
      setBelow(flip);
      setMovel(podeMover);
      setArrasto({ dx: 0, dy: 0 });
      setContent(node);
      overlay.setPosition(coordinate);
      marker.setPosition(withMarker ? coordinate : undefined);
    };
    const unregister = registerPopupOpener((coordinate, node, opts) => {
      const pixel = map.getPixelFromCoordinate(coordinate);
      show(coordinate, node, !!opts?.abaixo || (!!pixel && pixel[1] < POPUP_FLIP_PX), opts?.marker, opts?.movel, opts?.evitar ?? null);
    });
    const unregisterCloser = registerPopupCloser(close);

    let clickSeq = 0;
    const onClick = (evt: MapBrowserEvent) => {
      const seq = ++clickSeq;
      const hits: { feature: FeatureLike; layer: Layer; order: number }[] = [];
      map.forEachFeatureAtPixel(
        evt.pixel,
        (feature, layer) => {
          if (layer) hits.push({ feature, layer, order: hits.length });
          return undefined;
        },
        { hitTolerance: toleranciaDoClique(evt.originalEvent), layerFilter: hasPopup },
      );
      hits.sort((a, b) => (b.layer.getZIndex() ?? 0) - (a.layer.getZIndex() ?? 0) || a.order - b.order);
      for (const hit of hits) {
        const node = popupOf(hit.layer)?.(hit.feature);
        if (node !== null && node !== undefined && node !== false) {
          // Feição de ponto (foco, estação): a ficha se prende ao ponto, não ao lugar do clique (até
          // 10 px dele, na tolerância do toque): a seta e a linha de ligação apontam para ele.
          const geom = (hit.feature as Feature<Geometry>).getGeometry?.();
          const coord = geom?.getType() === 'Point' ? (geom as unknown as { getCoordinates(): number[] }).getCoordinates() : evt.coordinate;
          const px = map.getPixelFromCoordinate(coord) ?? evt.pixel;
          // Perto do topo do mapa o popup abre para baixo do ponto.
          // `popupAbaixo`: a ficha abre abaixo do ponto, presa pelo topo (a de abas muda de altura
          // ao trocar de aba; presa pelo pé, as abas pulariam).
          const abaixo = hit.layer.get('popupAbaixo') === true || px[1]! < POPUP_FLIP_PX;
          show(coord, node, abaixo, false, hit.layer.get('popupMovel') === true, evitarOf(hit.layer, hit.feature));
          return;
        }
      }
      // Clique no vazio com um popup aberto: só fecha (no celular, tocar para fechar).
      const wasOpen = overlay.getPosition() !== undefined;
      close();
      if (wasOpen) return;
      const [x, y] = evt.coordinate as [number, number];
      const flip = evt.pixel[1] < POPUP_FLIP_PX;
      // Camadas de imagem (radar) respondem pela coordenada; se nenhuma
      // responder e a camada de municípios estiver LIGADA, mostra o município
      // do ponto (o contorno só responde ao clique na linha). Com ela
      // desligada, nada — e os dados dos municípios não são baixados à toa.
      void coordinatePopupAt([x, y])
        .then((node) => {
          if (seq !== clickSeq || node === CONSUMIDO) return null;
          if (node) {
            show([x, y], node, flip);
            return null;
          }
          return useVectorStore.getState().visible.municipios ? municipioFeatureAt(x, y) : null;
        })
        .then((f) => {
          if (seq !== clickSeq || !f) return;
          const uf = f.get('uf');
          const cd = f.get('cd_mun');
          show(
            [x, y],
            <MunicipioCard
              nome={f.get('nome') as string}
              uf={typeof uf === 'string' ? uf : null}
              cdMun={cd != null ? String(cd) : null}
              lonLat={mapToLonLat(x, y)}
              geometry={f.getGeometry() ?? null}
            />,
            flip,
          );
        })
        .catch(() => {});
    };

    let raf = 0;
    let lastPixel: number[] | null = null;
    const onMove = (evt: MapBrowserEvent) => {
      if (evt.dragging) return;
      lastPixel = evt.pixel;
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        if (!lastPixel) return;
        const hit = map.hasFeatureAtPixel(lastPixel, { hitTolerance: HIT_TOLERANCE, layerFilter: hasPopup });
        map.getViewport().style.cursor = hit ? 'pointer' : '';
      });
    };
    map.on('singleclick', onClick);
    map.on('pointermove', onMove);
    return () => {
      map.un('singleclick', onClick);
      map.un('pointermove', onMove);
      map.un('change:size', aoRedimensionar);
      map.un('rendercomplete', conferir);
      map.un('moveend', conferir);
      map.un('pointerdrag', aoArrastarOMapa);
      viewport.removeEventListener('wheel', aoArrastarOMapa);
      observador?.disconnect();
      paineis?.disconnect();
      cancelAnimationFrame(conferencia);
      cancelAnimationFrame(deslize.current);
      ro?.disconnect();
      cancelAnimationFrame(raf);
      unregister();
      unregisterCloser();
      map.removeOverlay(overlay);
      map.removeOverlay(marker);
      overlayRef.current = null;
      markerRef.current = null;
      setContent(null);
    };
  }, [map, element, markerElement]);

  // Camada não-modal da pilha: Escape fecha o popup só se nenhum diálogo estiver por cima.
  useModalLayer(content !== null, {
    modal: false,
    onEscape: () => {
      overlayRef.current?.setPosition(undefined);
      markerRef.current?.setPosition(undefined);
      evitarRef.current = null;
      setContent(null);
    },
  });

  // Com o conteúdo já no DOM, garante que o popup inteiro fique visível.
  // Numa ficha arrastada, não: o usuário a pôs onde quer.
  const arrastada = arrasto.dx !== 0 || arrasto.dy !== 0;
  arrastadaRef.current = arrastada;
  useLayoutEffect(() => {
    if (content === null || !map) return;
    const r = element.querySelector('.amaview-popup')?.getBoundingClientRect();
    if (r) setTamanho({ largura: r.width, altura: r.height });
    // Ficha com área a evitar: em vez de andar o mapa até ela caber sobre o ponto, ela sai de
    // cima da área (também quando muda de forma). As demais fichas, como sempre.
    if (evitarRef.current?.()) desviarRef.current(true);
    else if (!arrastadaRef.current) enquadrar(map, element, 250);
    // A altura em que a ficha foi enquadrada na abertura: o reenquadrar ao crescer parte dela
    // (sem isso, a primeira medição dispararia uma segunda animação junto com esta).
    if (r) alturaEnquadrada.current = r.height;
  }, [content, map, element, forma]);

  const aoAbrirDetalhes = useCallback((abertos: boolean) => {
    detalhesAbertos.current = abertos;
  }, []);
  const contexto = useMemo(() => ({ forma, aoAbrirDetalhes }), [forma, aoAbrirDetalhes]);

  /** Arrasta pela alça: muda só o offset do overlay (a ficha segue presa ao ponto). */
  const aoPegar = (e: ReactPointerEvent<HTMLElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const alca = e.currentTarget;
    alca.setPointerCapture(e.pointerId);
    // Enquanto o usuário segura a ficha, nada a move (nem o player, nem um deslize em curso).
    segurando.current = true;
    cancelAnimationFrame(deslize.current);
    const x0 = e.clientX;
    const y0 = e.clientY;
    const inicio = { ...arrasto };
    // A ficha não sai da parte visível do mapa (acima da barra do tempo) nem
    // entra na coluna de botões da direita (celular).
    const caixa = alca.closest('.amaview-popup')?.getBoundingClientRect();
    const vp = map?.getViewport().getBoundingClientRect();
    const barra = document.querySelector<HTMLElement>('[data-timebar]')?.getBoundingClientRect();
    const fundo = vp ? Math.min(vp.bottom, barra && barra.height > 0 && barra.top > vp.top ? barra.top : vp.bottom) : Infinity;
    const m = 8;
    const coluna = medirColuna();
    // Deitado, o trilho de camadas da esquerda também (em pé a ficha pode cobri-lo).
    const trilho = vp ? medirTrilho(vp, coluna) : null;
    const lim =
      caixa && vp
        ? {
            dx: [inicio.dx - (caixa.left - vp.left - m), inicio.dx + (vp.right - m - caixa.right)],
            dy: [inicio.dy - (caixa.top - vp.top - m), inicio.dy + Math.max(0, fundo - m - caixa.bottom)],
          }
        : null;
    const prender = (v: number, [lo, hi]: number[]) => Math.min(Math.max(v, Math.min(lo!, hi!)), Math.max(lo!, hi!));
    let andou = false;
    const mover = (ev: PointerEvent) => {
      let dx = inicio.dx + ev.clientX - x0;
      let dy = inicio.dy + ev.clientY - y0;
      if (lim && caixa) {
        dy = prender(dy, lim.dy);
        const desceu = dy - inicio.dy;
        const direita = naAlturaDaColuna(caixa.top + desceu, caixa.bottom + desceu, coluna)
          ? Math.min(lim.dx[1]!, inicio.dx + (coluna!.left - FOLGA_DA_COLUNA_PX - caixa.right))
          : lim.dx[1]!;
        const esquerda = naAlturaDaColuna(caixa.top + desceu, caixa.bottom + desceu, trilho)
          ? Math.max(lim.dx[0]!, inicio.dx - (caixa.left - trilho!.right - FOLGA_DA_COLUNA_PX))
          : lim.dx[0]!;
        dx = prender(dx, [Math.min(esquerda, direita), direita]);
      }
      if (!andou && Math.hypot(ev.clientX - x0, ev.clientY - y0) < ARRASTO_MIN_PX) return;
      andou = true;
      postaAMao.current = true;
      overlayRef.current?.setOffset([baseOffset.current[0] + dx, baseOffset.current[1] + dy]);
      setArrasto({ dx, dy });
    };
    const soltar = () => {
      alca.removeEventListener('pointermove', mover);
      alca.removeEventListener('pointerup', soltar);
      alca.removeEventListener('pointercancel', soltar);
      segurando.current = false;
      // Largada em cima da área a evitar, a ficha desliza para fora; em qualquer outro lugar, fica.
      requestAnimationFrame(() => desviarRef.current(false));
    };
    alca.addEventListener('pointermove', mover);
    alca.addEventListener('pointerup', soltar);
    alca.addEventListener('pointercancel', soltar);
  };
  const devolver = () => {
    overlayRef.current?.setOffset(baseOffset.current);
    setArrasto({ dx: 0, dy: 0 });
    postaAMao.current = false;
    // Com área a evitar, "o ponto" está dentro dela: a ficha volta ao lugar em que abriu, ao lado.
    if (evitarRef.current) requestAnimationFrame(() => desviarRef.current(true));
  };
  const moverPeloTeclado = (e: ReactKeyboardEvent<HTMLElement>) => {
    const passo = e.shiftKey ? 40 : 10;
    const d = { ArrowLeft: [-passo, 0], ArrowRight: [passo, 0], ArrowUp: [0, -passo], ArrowDown: [0, passo] }[e.key];
    if (!d) return;
    e.preventDefault();
    e.stopPropagation();
    const novo = { dx: arrasto.dx + d[0]!, dy: arrasto.dy + d[1]! };
    overlayRef.current?.setOffset([baseOffset.current[0] + novo.dx, baseOffset.current[1] + novo.dy]);
    setArrasto(novo);
    postaAMao.current = true;
    // Com a tecla apertada (repetindo) a ficha é do usuário: a conferência vem ao soltar.
    segurando.current = true;
  };
  const soltarATecla = (e: ReactKeyboardEvent<HTMLElement>) => {
    if (!segurando.current || !e.key.startsWith('Arrow')) return;
    segurando.current = false;
    requestAnimationFrame(() => desviarRef.current(false));
  };

  if (content === null) return null;
  // A linha de ligação, nas coordenadas do SVG: a origem é o meio da borda de baixo da ficha (ou
  // de cima, aberta para baixo), que é por onde o overlay a prende ao ponto.
  const ponto = { x: -arrasto.dx, y: (below ? -1 : 1) * Math.abs(baseOffset.current[1]) - arrasto.dy };
  const caixa: Caixa = {
    left: -tamanho.largura / 2,
    right: tamanho.largura / 2,
    top: below ? 0 : -tamanho.altura,
    bottom: below ? tamanho.altura : 0,
  };
  const borda = pontoNaBorda(caixa, ponto);
  const ligacao = borda ? { borda, ponto } : null;
  return createPortal(
    <div
      className={`amaview-popup relative max-w-[min(23rem,88vw,var(--popup-largura,100vw))] border border-ink-600/80 bg-ink-900 rounded-xl px-3.5 ${movel ? 'pt-6 pb-3' : 'py-3'} text-sm text-ink-100 shadow-2xl${below ? ' below' : ''}${arrastada ? ' arrastado' : ''}`}
      data-popup-movel={movel ? '' : undefined}
      role="region"
      aria-label="Detalhes do ponto clicado"
    >
      {movel && arrastada && ligacao && (
        // A linha do ponto até a ficha: a ficha afastada continua dizendo de onde é. Encosta na
        // BORDA da ficha mais perto do ponto, nunca por dentro dela.
        <svg
          aria-hidden
          className="pointer-events-none absolute overflow-visible"
          style={{ left: '50%', top: below ? 0 : '100%', width: 1, height: 1 }}
          data-linha-do-ponto=""
        >
          <line
            x1={ligacao.borda.x}
            y1={ligacao.borda.y}
            x2={ligacao.ponto.x}
            y2={ligacao.ponto.y}
            stroke="rgba(127,227,255,0.85)"
            strokeWidth="1.5"
            strokeDasharray="4 3"
          />
          <circle cx={ligacao.ponto.x} cy={ligacao.ponto.y} r="3.5" fill="rgba(127,227,255,0.95)" stroke="rgba(8,16,24,0.8)" strokeWidth="1" />
        </svg>
      )}
      {movel && (
        <button
          type="button"
          aria-label="Mover a ficha (arraste; setas do teclado; duplo clique devolve ao ponto)"
          title="Arraste para mover a ficha · duplo clique devolve ao ponto"
          // No toque a área de pegar é maior que o desenho (44 px de altura).
          className="absolute top-1.5 left-1/2 z-10 inline-flex h-5 w-10 -translate-x-1/2 cursor-grab touch-none pointer-coarse:after:absolute pointer-coarse:after:-inset-x-3 pointer-coarse:after:-top-1.5 pointer-coarse:after:-bottom-4.5 items-center justify-center rounded-md text-ink-400 hover:bg-ink-700 hover:text-white focus-visible:outline-2 focus-visible:outline-mata-400 active:cursor-grabbing"
          onPointerDown={aoPegar}
          onDoubleClick={devolver}
          onKeyDown={moverPeloTeclado}
          onKeyUp={soltarATecla}
          onBlur={() => {
            segurando.current = false;
          }}
          data-alca-popup=""
        >
          <GripHorizontal className="size-4" aria-hidden />
        </button>
      )}
      <button
        type="button"
        aria-label="Fechar"
        title="Fechar"
        className="absolute top-1.5 right-1.5 z-10 inline-flex size-6 items-center justify-center rounded-md text-ink-300 after:absolute after:-inset-2.5 hover:bg-ink-700 hover:text-white focus-visible:outline-2 focus-visible:outline-mata-400"
        onClick={() => {
          overlayRef.current?.setPosition(undefined);
          markerRef.current?.setPosition(undefined);
          evitarRef.current = null;
          setContent(null);
        }}
      >
        <X className="size-4" aria-hidden />
      </button>
      {/* A rolagem fica aqui dentro, e não no popup: a seta (::after) vaza dele.
          As fichas têm largura própria (em vw): ao lado da coluna de botões do
          celular o popup é mais estreito, e elas encolhem com ele. */}
      <div className="-mr-2 max-h-[var(--popup-teto,calc(100dvh-8rem))] overflow-y-auto overscroll-contain pr-7 [&>*]:max-w-full">
        <ContextoDaFicha.Provider value={contexto}>{content}</ContextoDaFicha.Provider>
      </div>
    </div>,
    element,
  );
}
